// ═══════════════════════════════════════════════════════════════
// OpenGov EMC — Debris ADMS Type Definitions
// ═══════════════════════════════════════════════════════════════

// ── Debris Types & Measurement Modes ──────────────────────────
// Each debris type has a measurement mode that controls what fields
// appear on the load ticket form and how billing is calculated.

export type DebrisType =
  | 'vegetative' | 'c_and_d' | 'soil_mud_sand'
  | 'hazardous_tree' | 'stump' | 'white_goods'
  | 'e_waste' | 'hhw'
  // Non-traditional debris (unit/weight-based)
  | 'mattresses' | 'tires' | 'transformers'
  | 'vehicles' | 'vessels' | 'mixed';

export type MeasurementMode = 'volumetric' | 'unit' | 'weight';

export interface DebrisTypeConfig {
  label: string;
  color: string;
  measurementMode: MeasurementMode;
  /** Unit label for unit-based types (e.g., "each", "unit") */
  unitLabel?: string;
  /** Subcategory options for unit-based types */
  subcategories?: string[];
  /** Whether diameter range applies (stumps, hazardous trees) */
  hasDiameter?: boolean;
  /** Weight unit default (lbs or tons) */
  weightUnit?: 'lbs' | 'tons';
}

export const DEBRIS_TYPES: Record<DebrisType, DebrisTypeConfig> = {
  // Volumetric — measured in cubic yards via load% × truck capacity
  vegetative:      { label: 'Vegetative', color: '#16a34a', measurementMode: 'volumetric' },
  c_and_d:         { label: 'Construction & Demolition', color: '#d97706', measurementMode: 'volumetric' },
  soil_mud_sand:   { label: 'Soil / Mud / Sand', color: '#92400e', measurementMode: 'volumetric' },
  mixed:           { label: 'Mixed Debris', color: '#64748b', measurementMode: 'volumetric' },

  // Unit-based — measured by count (each), with optional subcategory/diameter
  hazardous_tree:  { label: 'Hazardous Trees (Leaners/Hangers)', color: '#dc2626', measurementMode: 'unit', unitLabel: 'trees', hasDiameter: true },
  stump:           { label: 'Stump Removal', color: '#7c3aed', measurementMode: 'unit', unitLabel: 'stumps', hasDiameter: true,
                     subcategories: ['6-12"', '12-24"', '24-36"', '36-48"', '48"+'] },
  white_goods:     { label: 'White Goods (Appliances)', color: '#0284c7', measurementMode: 'unit', unitLabel: 'units',
                     subcategories: ['Refrigerator', 'Freezer', 'Washer', 'Dryer', 'Dishwasher', 'AC Unit', 'Water Heater', 'Stove/Range', 'Other'] },
  mattresses:      { label: 'Mattresses', color: '#a855f7', measurementMode: 'unit', unitLabel: 'mattresses' },
  tires:           { label: 'Tires', color: '#475569', measurementMode: 'unit', unitLabel: 'tires',
                     subcategories: ['Passenger', 'Light Truck', 'Commercial/Semi', 'OTR/Heavy Equipment'] },
  transformers:    { label: 'Transformers', color: '#b45309', measurementMode: 'unit', unitLabel: 'units',
                     subcategories: ['Tested Clean', 'Untested', 'PCB-Containing'] },
  vehicles:        { label: 'Vehicles', color: '#334155', measurementMode: 'unit', unitLabel: 'vehicles',
                     subcategories: ['Passenger Car', 'Truck/SUV', 'Motorcycle', 'RV/Camper', 'Commercial Vehicle'] },
  vessels:         { label: 'Vessels / Boats', color: '#0369a1', measurementMode: 'unit', unitLabel: 'vessels',
                     subcategories: ['< 16 ft', '16-26 ft', '26-40 ft', '40+ ft'] },

  // Weight-based — measured in lbs or tons
  e_waste:         { label: 'Electronic Waste', color: '#6366f1', measurementMode: 'weight', weightUnit: 'lbs' },
  hhw:             { label: 'Household Hazardous Waste', color: '#be123c', measurementMode: 'weight', weightUnit: 'lbs' },
};

// Legacy convenience exports for backward compat
export const DEBRIS_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  Object.entries(DEBRIS_TYPES).map(([k, v]) => [k, v.label])
);
export const DEBRIS_TYPE_COLORS: Record<string, string> = Object.fromEntries(
  Object.entries(DEBRIS_TYPES).map(([k, v]) => [k, v.color])
);

// Dropdown options
export const DEBRIS_TYPE_OPTIONS = Object.entries(DEBRIS_TYPES).map(([value, cfg]) => ({
  value,
  label: `${cfg.label}${cfg.measurementMode !== 'volumetric' ? ` (${cfg.measurementMode === 'unit' ? 'count' : 'weight'})` : ''}`,
}));

// Diameter range options for stumps and hazardous trees (per FEMA schedule)
export const DIAMETER_RANGES = ['6-12"', '12-24"', '24-36"', '36-48"', '48"+'];

export type ZoneStatus = 'pending' | 'active' | 'completed' | 'closed';
export type ContractorStatus = 'active' | 'suspended' | 'completed';
export type CrewStatus = 'active' | 'standby' | 'out_of_service';
export type TruckStatus = 'certified' | 'pending' | 'decertified';
// Enhanced ticket status with verification workflow
export type TicketStatus = 'draft' | 'in_transit' | 'at_site' | 'verified' | 'delivered' | 'disputed' | 'voided';
export type SiteType = 'tdsr' | 'dms' | 'final_disposal' | 'recycler';
export type SiteStatus = 'open' | 'full' | 'closed';
export type InvoiceStatus = 'draft' | 'submitted' | 'approved' | 'paid' | 'disputed';
export type DocCategory = 'safety' | 'procedures' | 'maps' | 'contracts' | 'fema' | 'other';
export type TruckType = 'trailer' | 'dump_truck' | 'knuckleboom' | 'roll_off' | 'self_loader';
export type CrewType = 'knuckleboom' | 'grapple' | 'self_loader' | 'dump_truck';

// ── Ticket Status Flow ──────────────────────────────────────
export const TICKET_STATUS_LABELS: Record<TicketStatus, string> = {
  draft: 'Draft',
  in_transit: 'In Transit',
  at_site: 'At Disposal Site',
  verified: 'Verified by Monitor',
  delivered: 'Delivered',
  disputed: 'Disputed',
  voided: 'Voided',
};

export const TICKET_STATUS_COLORS: Record<TicketStatus, string> = {
  draft: '#94a3b8',
  in_transit: '#3b82f6',
  at_site: '#f59e0b',
  verified: '#16a34a',
  delivered: '#059669',
  disputed: '#ef4444',
  voided: '#6b7280',
};

// ── Pickup Stop (field crew captures these along a route) ────
export interface PickupStop {
  id: string;              // local UUID
  address: string;
  lat: number;
  lng: number;
  debrisType: DebrisType;
  measurementMode: MeasurementMode;
  // Volumetric
  estimatedLoadPercent?: number;
  // Unit-based
  unitCount?: number;
  unitSubcategory?: string;
  diameterRange?: string;
  // Weight-based
  weight?: number;
  weightUnit?: 'lbs' | 'tons';
  // Photos + notes
  beforePhotoData?: string;  // base64
  afterPhotoData?: string;   // base64
  notes: string;
  timestamp: string;
}

export interface DebrisZone {
  id: number;
  Rid: number;
  incidentId: string;
  zoneName: string;
  zoneCode: string;
  status: ZoneStatus;
  boundaryGeojson: string;
  centerLat: string;
  centerLng: string;
  estimatedCubicYards: string;
  actualCubicYards: string;
  debrisTypes: string;       // JSON array
  assignedContractorId: string;
  notes: string;
  creatorEmail: string;
  currentPass: string;      // '1', '2', '3', 'final'
  passHistory: string;      // JSON: [{ pass: '1', startedAt, completedAt, notes }]
}

export interface DebrisContractor {
  id: number;
  Rid: number;
  incidentId: string;
  companyName: string;
  contractNumber: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  rateVegetative: string;
  rateCAndD: string;
  rateSoilMudSand: string;
  rateHazardousTree: string;
  rateStumpRemoval: string;  // JSON by diameter range
  rateWhiteGoods: string;
  rateElectronic: string;
  rateHhw: string;
  status: ContractorStatus;
  notes: string;
}

export interface DebrisCrew {
  id: number;
  Rid: number;
  contractorId: string;
  incidentId: string;
  crewName: string;
  crewType: string;
  truckId: string;
  driverName: string;
  driverPhone: string;
  driverEmail: string;
  status: CrewStatus;
  assignedZoneId: string;
  notes: string;
}

export interface DebrisTruck {
  id: number;
  Rid: number;
  contractorId: string;
  incidentId: string;
  truckNumber: string;
  licensePlate: string;
  truckType: string;
  lengthFt: string;
  widthFt: string;
  heightFt: string;
  cubicYardCapacity: string;
  certifiedDate: string;
  certifiedByName: string;
  certPhotoUrl: string;
  qrCode: string;
  status: TruckStatus;
  notes: string;
  hasTailgate: string;         // 'true'/'false' — no tailgate = 15% FEMA capacity reduction
  certExpiresAt: string;       // re-certification due date
  certStatus: string;          // 'certified' | 'expired' | 'pending'
  placardNumber: string;       // physical placard ID on the truck
  bedPhotoUrl: string;         // photo of the measured truck bed
}

export interface DebrisLoadTicket {
  id: number;
  Rid: number;
  incidentId: string;
  ticketNumber: string;
  truckId: string;
  truckNumber: string;
  crewId: string;
  contractorId: string;
  zoneId: string;
  disposalSiteId: string;
  debrisType: string;
  // ── Measurement (mode determines which fields are used) ────
  measurementMode: string;     // 'volumetric' | 'unit' | 'weight'
  // Volumetric fields
  loadPercentage: string;
  calculatedCubicYards: string;
  // Unit-based fields
  unitCount: string;
  unitSubcategory: string;
  diameterRange: string;       // for stumps/hazardous trees
  // Weight-based fields
  weight: string;
  weightUnit: string;          // 'lbs' | 'tons'
  scaleTicketNumber: string;
  // ── Pickup info ────────────────────────────────────────────
  pickupLat: string;
  pickupLng: string;
  pickupAddress: string;
  pickupTime: string;
  pickupStopsJson: string;     // JSON array of PickupStop[] from field crew
  // ── Disposal info ──────────────────────────────────────────
  disposalTime: string;
  // ── FEMA Chain of Custody ───────────────────────────────────
  // ── FEMA Chain of Custody (4 signatures) ────────────────────
  // At pickup — loading site monitor + driver
  loadingMonitorName: string;
  loadingMonitorSignature: string;  // base64 — loading monitor signs at pickup
  driverPickupSignature: string;    // base64 — driver signs at pickup
  loadingLoadPct: string;           // loading monitor's visual estimate %
  // At disposal — tower/site monitor + driver
  towerLoadPct: string;             // tower monitor's independent estimate %
  monitorName: string;              // tower monitor name
  monitorSignature: string;         // base64 — tower monitor signs at disposal
  driverSignature: string;          // base64 — driver signs at disposal
  verifiedAt: string;
  handLoaded: string;               // 'true' → FEMA 50% capacity cap
  // ── QR & status ────────────────────────────────────────────
  qrCode: string;              // encoded ticket identifier for QR
  status: TicketStatus;
  // ── Photos ─────────────────────────────────────────────────
  photoBeforeUrl: string;
  photoAfterUrl: string;
  photoLoadUrl: string;
  notes: string;
}

export interface DebrisDisposalSite {
  id: number;
  Rid: number;
  incidentId: string;
  siteName: string;
  siteType: string;
  address: string;
  latitude: string;
  longitude: string;
  capacityCubicYards: string;
  currentVolume: string;
  acceptedTypes: string;     // JSON array
  operatingHours: string;
  monitorName: string;
  monitorEmail: string;
  status: SiteStatus;
  notes: string;
}

export interface DebrisPhoto {
  id: number;
  Rid: number;
  parentId: string;
  parentType: string;
  incidentId: string;
  photoUrl: string;
  caption: string;
  geoLat: string;
  geoLng: string;
  takenAt: string;
  uploadedBy: string;
}

export interface DebrisAuditEntry {
  id: number;
  Rid: number;
  incidentId: string;
  entityType: string;
  entityId: string;
  action: string;
  fieldChanged: string;
  oldValue: string;
  newValue: string;
  actorName: string;
  actorEmail: string;
  actorRole: string;
}

export interface DebrisDocument {
  id: number;
  Rid: number;
  incidentId: string;
  title: string;
  category: string;
  description: string;
  fileUrl: string;
  uploadedBy: string;
  visibleTo: string;         // JSON array of role slugs
  status: string;
}

export interface DebrisInvoice {
  id: number;
  Rid: number;
  incidentId: string;
  contractorId: string;
  invoiceNumber: string;
  periodStart: string;
  periodEnd: string;
  totalCubicYards: string;
  totalAmount: string;
  ticketIds: string;         // JSON array
  lineItems: string;         // JSON array
  status: InvoiceStatus;
  approvedBy: string;
  notes: string;
}

export type DebrisRequestStatus = 'pending' | 'actioned' | 'dismissed';

export interface DebrisRequest {
  id: number;
  Rid: number;
  incidentId: string;
  assessmentId: string;
  address: string;
  latitude: string;
  longitude: string;
  damageCategory: string;
  damageTypes: string;       // JSON array
  description: string;
  status: DebrisRequestStatus;
  taggedBy: string;
  taggedAt: string;
  actionedBy: string;
  actionedAt: string;
  createdTicketId: string;
  notes: string;
}

export interface DebrisStats {
  totalTickets: number;
  totalCubicYards: number;
  totalUnits: number;           // sum of unitCount across unit-based tickets
  totalWeight: number;          // sum of weight (normalized to lbs)
  cubicYardsByType: Record<string, number>;
  unitsByType: Record<string, number>;
  weightByType: Record<string, number>;
  cubicYardsByZone: Record<string, number>;
  ticketsByStatus: Record<string, number>;
  loadsToday: number;
  activeTrucks: number;
  activeCrews: number;
  estimatedCompletion: number; // percentage
}
